import { toast } from 'utils/toast';
import { useCallback, useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { Link, Upload, UploadProps, Modal, Button, Paragraph } from '@ets/design-system';
import { useDispatch, useSelector } from '_common/hooks';
import { EditorService, InstanceService } from '_common/services';
import { closeModal, openAndUpdateModal } from '_common/modals/ModalsSlice';
import { setLoadingValue } from 'Editor/redux/EditorStatusSlice';

import Loading from './Loading/Loading';

import { useSuiteUtils } from '_common/suite';

// Current state of the modal. The modal can only be in one of these states simultaneously.
const CURRENT_STATE = {
  INITIAL: 1, // Upload a file state
  LOADING: 2, // Loading state
  ERROR: 3, // Error state
  UPLOADED: 4, // Post uploaded file state
};

const ImportNewVersionModal = () => {
  const dispatch = useDispatch();
  const refetchObject = useSuiteUtils();

  const isOpen = useSelector((state) => state.modals.open.ImportNewVersionModal);
  const document = useSelector((state) => state.editor.status.documentId);

  const [currentState, setCurrentState] = useState(CURRENT_STATE.INITIAL);
  const [file, setFile] = useState<File>();

  useEffect(() => {
    if (file?.name) {
      setCurrentState(CURRENT_STATE.UPLOADED);
    }
  }, [file]);

  const importNewVersion = useCallback(async (documentId: string, file: File) => {
    const { data } = await new EditorService().importDocument(documentId, file);

    if (data?.id) {
      const jobId = data.id;
      let timeout: NodeJS.Timeout | null = null;

      dispatch(setLoadingValue('EDITOR_PROCESSING_STATE'));
      timeout = setInterval(async () => {
        try {
          const { data } = await new InstanceService().checkJobStatus({ jobs: [jobId] });
          // @ts-expect-error Missing endpoint type "/api/jobs/get"
          switch (data[jobId].status) {
            case 'queued':
            case 'started':
              return;
            case 'finished':
              // @ts-expect-error Missing endpoint type "/api/jobs/get"
              if (data[jobId]?.result[1] === 'broken') {
                dispatch(
                  openAndUpdateModal({
                    modal: 'ConfirmationModal',
                    data: {
                      title: 'EDITOR_BROKEN_STATE_TITLE',
                      message: 'EDITOR_BROKEN_STATE_MESSAGE',
                      confirmButtonTextId: 'editor.menu.file.backToExplorer',
                      confirmButtonType: '',
                      actionCode: 'backToExplorer',
                      cancelButtonShow: false,
                      persistent: true,
                    },
                  }),
                );
              } else {
                refetchObject();
                toast({
                  variant: 'success',
                  title: 'NEW_DOCUMENT_VERSION_IMPORTED',
                  message: 'DOCX_SUCCESSFULLY_IMPORTED_AS_NEW_VERSION',
                });
              }

              if (timeout) {
                // After RT process the document, new data is fetched and re-rendered
                dispatch(setLoadingValue(true));
                clearInterval(timeout);
              }

              return;
            case 'failed':
              if (timeout) {
                dispatch(setLoadingValue(false));
                clearInterval(timeout);
              }

              break;

            default:
          }
        } catch (error) {
          if (timeout) {
            dispatch(setLoadingValue(false));
            clearInterval(timeout);
          }
        }
      }, 2500);
    }
  }, []);

  // Reset the modal the the initial state
  const resetState = () => {
    setCurrentState(CURRENT_STATE.INITIAL);
    setFile(undefined);
  };

  // Proccess the file upload
  const handleUpload: UploadProps['onDrop'] = async (acceptedFiles) => {
    if (
      !acceptedFiles[0] ||
      (acceptedFiles[0].type && acceptedFiles[0].name.indexOf('docx')) === -1
    ) {
      setCurrentState(CURRENT_STATE.ERROR);
      return;
    }

    const reader = new FileReader();
    reader.addEventListener('progress', () => {
      setCurrentState(CURRENT_STATE.LOADING);
    });

    reader.addEventListener('load', () => {
      setFile(acceptedFiles[0]);
    });

    reader.readAsDataURL(acceptedFiles[0]);
  };

  const importDocument = async () => {
    if (file) {
      importNewVersion(document, file);
      close();
    }
  };

  const close = () => {
    resetState();
    dispatch(closeModal('ImportNewVersionModal'));
  };

  return (
    <Modal open={!!isOpen} width="60rem" close={close} testId="import-new-version">
      <Modal.Header>
        <FormattedMessage id="IMPORT_NEW_VERSION" />
      </Modal.Header>
      <Modal.Body style={{ overflow: 'visible' }}>
        {currentState === CURRENT_STATE.INITIAL && (
          <Upload
            size="m"
            icon="cloud-arrow-up"
            name="upload"
            data-testid="inputUpload"
            accept={{
              'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
            }}
            onDrop={handleUpload}
            testId="upload-new-version-input"
            className="w-full"
          >
            <div className="flex gap-2">
              <FormattedMessage id="DRAG_AND_DROP_DOCX_FILE" />
              <FormattedMessage id="global.or" />
              <Link variant="primary" size="m" testId={`upload-label`}>
                <FormattedMessage id="BROWSE_YOUR_COMPUTER" />
              </Link>
            </div>
          </Upload>
        )}
        {currentState === CURRENT_STATE.LOADING && (
          <Loading
            message={{ id: 'storage.actionBar.new.importingDocument' }}
            icon="file-lines"
            testId="loading-icon"
          />
        )}
        {currentState === CURRENT_STATE.ERROR && (
          <div className="bg-red-5 border-red-60 flex flex-col items-center rounded-2xl border-2 px-40 pt-32 pb-22">
            <Paragraph size="m" weight="500" className="text-center">
              <FormattedMessage id="INVALID_FORMAT_IMPORT_ONLY_DOCX" />
            </Paragraph>

            <div className="w-full px-14">
              <Button
                size="m"
                variant="primary"
                onClick={resetState}
                fullWidth
                style={{ margin: '4rem 0 0 0' }}
                testId="import-new-version-import-diferent-file"
              >
                <FormattedMessage id="IMPORT_DIFFERENT_FILE" />
              </Button>
            </div>
          </div>
        )}
        {currentState === CURRENT_STATE.UPLOADED && (
          <div className="leading-12">
            <FormattedMessage
              id="YOU_ARE_IMPORTING_WORD_TO_BE_CURRENT_VERSION"
              values={{ name: file?.name }}
            />
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button size="m" variant="standard" onClick={close} testId="import-new-version-cancel">
          <FormattedMessage id="global.cancel" />
        </Button>
        {currentState === CURRENT_STATE.UPLOADED && (
          <Button
            size="m"
            variant="primary"
            style={{ margin: '0 0 0 1rem' }}
            onClick={importDocument}
            testId="import-new-version-submit"
          >
            <FormattedMessage id="IMPORT_DOCX_AS_A_NEW_VERSION" />
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default ImportNewVersionModal;
